import { ReactElement, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Col, Card, Popconfirm, Space, Switch, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import type { Application } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import {
  DeleteHttpIntegrationRequest,
  GetHttpIntegrationRequest,
  HttpIntegration,
  UpdateHttpIntegrationRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";

import ApplicationStore from "../../../stores/ApplicationStore";

interface IProps {
  application: Application;
  add?: boolean;
}

function HttpCard(props: IProps) {
  const [integration, setIntegration] = useState<HttpIntegration | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (props.add) {
      return;
    }

    const req = new GetHttpIntegrationRequest();
    req.setApplicationId(props.application.getId());

    ApplicationStore.getHttpIntegration(req, (resp) => {
      setIntegration(resp.getIntegration());
    });
  }, [props.add, props.application]);

  const onDelete = () => {
    const req = new DeleteHttpIntegrationRequest();
    req.setApplicationId(props.application.getId());
    ApplicationStore.deleteHttpIntegration(req, () => {});
  };

  const onPauseToggle = (checked: boolean) => {
    if (!integration) {
      return;
    }

    setSaving(true);

    const v = integration.toObject();
    const i = new HttpIntegration();
    i.setApplicationId(v.applicationId);
    i.setEncoding(v.encoding);
    i.setEventEndpointUrl(v.eventEndpointUrl);
    i.setPaused(checked);
    for (const elm of v.headersMap) {
      i.getHeadersMap().set(elm[0], elm[1]);
    }

    const req = new UpdateHttpIntegrationRequest();
    req.setIntegration(i);
    ApplicationStore.updateHttpIntegration(req, () => {
      setIntegration(i);
      setSaving(false);
    });
  };

  let actions: ReactElement[] = [];

  if (props.add) {
    actions = [
      <Link to="http/create">
        <PlusOutlined />
      </Link>,
    ];
  } else {
    actions = [
      <Link to="http/edit">
        <EditOutlined />
      </Link>,
      <Popconfirm title="Are you sure you want to delete this integration?" onConfirm={onDelete}>
        <DeleteOutlined />
      </Popconfirm>,
    ];
  }

  return (
    <Col span={8}>
      <Card
        title="HTTP"
        className="integration-card"
        cover={<img alt="HTTP" src="/integrations/http.png" style={{ padding: 1 }} />}
        actions={actions}
      >
        <Card.Meta description="The HTTP integration forwards events to a user-configurable endpoint as POST requests." />
        {!props.add && (
          <Space style={{ marginTop: 12 }}>
            <Switch
              checked={integration?.getPaused() ?? false}
              loading={saving}
              onChange={onPauseToggle}
              checkedChildren="Paused"
              unCheckedChildren="Active"
            />
            {integration?.getPaused() && <Tag color="warning">Paused</Tag>}
          </Space>
        )}
      </Card>
    </Col>
  );
}

export default HttpCard;
